import { SiteLink, PageSectionBackgroundColor } from '../../app-types'

/**
 * Resolves the `variant` for a link rendered inside the image-with-text
 * section. Paired hero CTAs default to two filled buttons, so an `auto`
 * variant resolves to `contained` regardless of position. Explicit author
 * variants pass through unchanged.
 */
function resolveVariant(variant: SiteLink['variant']): SiteLink['variant'] {
  return variant === 'auto' ? 'contained' : variant
}

/**
 * Auto-picks button colors for paired CTAs based on the section's
 * background color, so authors don't have to spell out `color` per link.
 *
 *  - Black section: white button first, primary (green) button second.
 *  - Any other section: black button first, white button second.
 *
 * Only applies when the resolved variant is `contained` — outlined/text
 * buttons keep their existing theme-based contrast behavior from
 * `LinkButton`. An explicit `link.color` always wins.
 */
export function calculateLinkProps(
  link: SiteLink,
  i: number,
  backgroundColor: PageSectionBackgroundColor | null,
): { variant: SiteLink['variant']; color?: SiteLink['color'] } {
  const variant = resolveVariant(link.variant)

  if (link.color) return { variant, color: link.color }
  if (variant !== 'contained') return { variant, color: undefined }

  const onBlack = backgroundColor === 'black'
  const color: SiteLink['color'] = onBlack
    ? i === 0
      ? 'inverse'
      : 'primary'
    : i === 0
      ? 'secondary'
      : 'inverse'

  return { variant, color }
}
